import { useEffect, useRef, useState } from 'react'
import {
    getSummary,
    getFollowups,
    search,
    getContacts,
    getContact,
    getActivities,
    getTasks,
    getDeals,
    getAccount,
    getViews,
    getDeal,
    getForecast,
    getAccounts,
    quoteProposal,
} from 'utility/services/crm'
import { isQuoteReady, quoteBody } from 'utility/crm/proposal'
import { toApiError } from 'utility/crm/api-error'
import { useDebounce } from 'hooks/debounce'
import { useCrmQuery } from './useCrmQuery'

/** @typedef {import('utility/crm/types').CrmContactsQuery} CrmContactsQuery */

export const useCrmSummary = () => useCrmQuery(() => getSummary(), [], { scopes: ['summary', 'contacts', 'deals'] })

/** @param {boolean} mine */
export const useFollowups = mine => useCrmQuery(() => getFollowups(mine), [mine], { scopes: ['followups', 'tasks'] })

/** @param {CrmContactsQuery} query */
export const useContacts = query =>
    useCrmQuery(() => getContacts(query), [JSON.stringify(query)], { scopes: ['contacts'] })

/** @param {number|null} id */
export const useContact = id =>
    useCrmQuery(() => getContact(id), [id], { scopes: ['contact', 'contacts'], enabled: Boolean(id) })

/** @param {number|null} contactId */
export const useContactActivities = contactId =>
    useCrmQuery(() => getActivities({ contactId, take: 100 }), [contactId], {
        scopes: ['activities'],
        enabled: Boolean(contactId),
    })

/** @param {number|null} contactId */
export const useContactTasks = contactId =>
    useCrmQuery(() => getTasks({ contactId, includeDone: true }), [contactId], {
        scopes: ['tasks'],
        enabled: Boolean(contactId),
    })

/** @param {number|null} contactId */
export const useContactDeals = contactId =>
    useCrmQuery(() => getDeals({ contactId }), [contactId], { scopes: ['deals'], enabled: Boolean(contactId) })

/** @param {number|null} accountId */
export const useAccountDetail = accountId =>
    useCrmQuery(() => getAccount(accountId), [accountId], {
        scopes: ['accounts', 'deals', 'contacts'],
        enabled: Boolean(accountId),
    })

export const useSavedViews = () => useCrmQuery(() => getViews(), [], { scopes: ['views'] })

/**
 * Global search: waits 250 ms after typing stops, needs 2+ characters.
 * @param {string} text
 */
export const useCrmSearch = text => {
    const debounced = useDebounce(text.trim(), 250)
    const active = debounced.length >= 2
    const [state, setState] = useState({ results: [], loading: false, error: null })
    const requestId = useRef(0)

    useEffect(() => {
        requestId.current += 1
        const id = requestId.current
        if (!active) {
            setState({ results: [], loading: false, error: null })
            return undefined
        }
        setState(prev => ({ ...prev, loading: true, error: null }))
        search(debounced)
            .then(results => {
                if (id === requestId.current) setState({ results, loading: false, error: null })
            })
            .catch(err => {
                if (id === requestId.current) setState({ results: [], loading: false, error: toApiError(err) })
            })
        return () => {
            // A newer search (or unmount) supersedes this one.
            requestId.current += 1
        }
    }, [debounced, active])

    return { ...state, active }
}

/** @param {import('utility/crm/types').CrmDealsQuery} query */
export const useDeals = query =>
    useCrmQuery(() => getDeals(query), [JSON.stringify(query)], { scopes: ['deals'] })

/** @param {string} pipeline */
export const useForecast = pipeline =>
    useCrmQuery(() => getForecast(pipeline), [pipeline], { scopes: ['forecast', 'deals'] })

/** @param {number|null} id */
export const useDeal = id =>
    useCrmQuery(() => getDeal(id), [id], { scopes: ['deals'], enabled: Boolean(id) })

/** @param {number|null} dealId */
export const useDealActivities = dealId =>
    useCrmQuery(() => getActivities({ dealId, take: 100 }), [dealId], {
        scopes: ['activities', 'deals'],
        enabled: Boolean(dealId),
    })

/**
 * Live price while typing: waits 300 ms after the last change, ignores out-of-order answers, saves nothing.
 * @param {import('utility/crm/types').CrmQuoteInput} quote
 */
export const useProposalQuote = quote => {
    const debounced = useDebounce(JSON.stringify(quoteBody(quote)), 300)
    const ready = isQuoteReady(quote)
    const [state, setState] = useState({ proposal: null, loading: false, error: null })
    const requestId = useRef(0)

    useEffect(() => {
        requestId.current += 1
        const id = requestId.current
        if (!ready) {
            setState(prev => ({ ...prev, loading: false, error: null }))
            return
        }
        setState(prev => ({ ...prev, loading: true, error: null }))
        quoteProposal(JSON.parse(debounced))
            .then(proposal => {
                if (id === requestId.current) setState({ proposal, loading: false, error: null })
            })
            .catch(err => {
                if (id === requestId.current) setState(prev => ({ ...prev, loading: false, error: toApiError(err) }))
            })
    }, [debounced, ready])

    // While the user is still typing (debounce pending) the shown price is stale: say so.
    const pending = ready && debounced !== JSON.stringify(quoteBody(quote))
    return { ...state, ready, stale: pending || state.loading }
}

/** @param {{search?: string, type?: string, ownerUserId?: number, sort?: 'risk'|'name'}} query */
export const useAccounts = query =>
    useCrmQuery(() => getAccounts(query), [JSON.stringify(query)], { scopes: ['accounts', 'deals', 'contacts'] })

/** @param {number|null} accountId */
export const useAccountActivities = accountId =>
    useCrmQuery(() => getActivities({ accountId, take: 100 }), [accountId], {
        scopes: ['activities'],
        enabled: Boolean(accountId),
    })
